/**
 * Staff-only cache observability at /staff/cache/.
 *
 * Read-only window onto the rvn:* Redis read-through cache
 * (apps/core/cache.py): aggregate stats plus a filterable/searchable table of
 * cached keys with their parsed identity, TTL and (opt-in) size. Metadata only
 * - never a decoded value, never a non-rvn: key.
 *
 * Backed by GET /api/staff/cache/stats/ and /keys/. Both fail open, so when the
 * backend is not Redis (or Redis is unreachable) the page shows a clean
 * unavailable panel instead of erroring.
 */

import { useEffect, useMemo, useState } from 'react'
import { Inbox, ServerOff } from 'lucide-react'

import {
  DataTable,
  type DataTableColumn,
  type SortingState,
} from '@/admin/components/data-table'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  useCacheKeys,
  useCacheStats,
  type CacheKeyRow,
  type CacheStats,
} from './staff-cache-api'

const ALL = '__all__'

export function CacheObservabilityPage() {
  const { formatNumber } = useFormatting()

  const [tenant, setTenant] = useState('')
  const [namespace, setNamespace] = useState('')
  const [entityType, setEntityType] = useState('')
  const [kind, setKind] = useState('')
  const [withSize, setWithSize] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sorting, setSorting] = useState<SortingState | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  const stats = useCacheStats()
  const statsData = stats.data
  const available = statsData?.available
  // Entity type only applies to the entity namespace.
  const effectiveEntityType = namespace === 'entity' ? entityType : ''

  const keys = useCacheKeys(
    {
      tenant,
      namespace,
      entity_type: effectiveEntityType,
      kind,
      search: debouncedSearch,
      withSize,
      limit: 500,
    },
    // Do not fetch keys once we know the backend is unavailable.
    available !== false,
  )
  const keysData = keys.data

  // Entity-type options are derived from live rows (the stats payload does not
  // carry them); union the current selection so it never vanishes mid-browse.
  const entityTypeOptions = useMemo(() => {
    const set = new Set<string>()
    for (const row of keysData?.rows ?? []) {
      if (row.entity_type) set.add(row.entity_type)
    }
    if (entityType) set.add(entityType)
    return Array.from(set).sort()
  }, [keysData, entityType])

  const rows = useMemo(
    () => sortRows(keysData?.rows ?? [], sorting),
    [keysData, sorting],
  )

  const columns = useMemo<DataTableColumn<CacheKeyRow>[]>(
    () => buildColumns(),
    [],
  )

  const hasFilters = Boolean(
    tenant || namespace || effectiveEntityType || kind || debouncedSearch,
  )

  return (
    <StaffShell>
      <div className="mx-auto mt-8 max-w-[1200px] px-6 pb-12">
        <div className="mb-6">
          <h1 className="font-display text-[28px] leading-tight text-[var(--ink)]">
            Redis cache
          </h1>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            Read-only view of the Redis read-through cache (rvn:* keys). Shows
            metadata only - key parts, TTL and size, never a cached value.
          </p>
        </div>

        {available === false ? (
          <Unavailable reason={statsData?.reason} backend={statsData?.backend} />
        ) : (
          <>
            <StatTiles stats={statsData} formatNumber={formatNumber} />

            {/* Filters: the two requested controls (tenant + namespace/entity)
                plus search and kind. */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Select
                value={tenant || ALL}
                onValueChange={(v) => setTenant(v === ALL ? '' : v)}
              >
                <SelectTrigger className="w-52" data-testid="cache-filter-tenant">
                  <SelectValue placeholder="Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All tenants</SelectItem>
                  {(statsData?.tenants ?? []).map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>
                      {(t.tenant_name ?? `Tenant ${t.tenant_id}`) +
                        ` (${formatNumber(t.keys)})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={namespace || ALL}
                onValueChange={(v) => {
                  setNamespace(v === ALL ? '' : v)
                  if (v !== 'entity') setEntityType('')
                }}
              >
                <SelectTrigger className="w-44" data-testid="cache-filter-namespace">
                  <SelectValue placeholder="Namespace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All namespaces</SelectItem>
                  {(statsData?.namespaces ?? []).map((n) => (
                    <SelectItem key={n.namespace} value={n.namespace}>
                      {n.namespace}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={entityType || ALL}
                onValueChange={(v) => setEntityType(v === ALL ? '' : v)}
                disabled={namespace !== 'entity'}
              >
                <SelectTrigger className="w-44" data-testid="cache-filter-entity-type">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All entity types</SelectItem>
                  {entityTypeOptions.map((et) => (
                    <SelectItem key={et} value={et}>
                      {et}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={kind || ALL}
                onValueChange={(v) => setKind(v === ALL ? '' : v)}
              >
                <SelectTrigger className="w-36" data-testid="cache-filter-kind">
                  <SelectValue placeholder="Kind" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All kinds</SelectItem>
                  <SelectItem value="data">Data</SelectItem>
                  <SelectItem value="version">Version</SelectItem>
                </SelectContent>
              </Select>

              <div className="w-64">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search object id or key"
                />
              </div>

              <Button
                variant={withSize ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setWithSize((s) => !s)}
              >
                {withSize ? 'Size: on' : 'Size: off'}
              </Button>
            </div>

            {keysData?.truncated ? (
              <p className="mb-2 text-[11px] text-[var(--warning-fg,#d97706)]">
                Showing a partial scan ({formatNumber(keysData.scanned)} keys
                walked). Refine the filters to narrow results.
              </p>
            ) : null}

            <DataTable<CacheKeyRow>
              tableId="staff-cache-keys"
              columns={columns}
              data={rows}
              rowKey={cacheRowKey}
              sorting={sorting}
              onSortingChange={setSorting}
              isLoading={keys.isPending}
              isFetching={keys.isFetching}
              isError={keys.isError}
              onRetry={() => keys.refetch()}
              emptyStateNode={
                <Empty
                  message={
                    hasFilters
                      ? 'No cached keys match your filters'
                      : 'No cached keys found'
                  }
                />
              }
            />
          </>
        )}
      </div>
    </StaffShell>
  )
}

// ---------------------------------------------------------------------------
// Stat tiles
// ---------------------------------------------------------------------------

function StatTiles({
  stats,
  formatNumber,
}: {
  stats: CacheStats | undefined
  formatNumber: (n: number) => string
}) {
  const hitRate =
    stats?.hit_rate != null ? `${(stats.hit_rate * 100).toFixed(1)}%` : '-'
  const totalKeys = stats?.dbsize != null ? formatNumber(stats.dbsize) : '-'
  const memory =
    stats?.used_memory != null
      ? `${formatBytes(stats.used_memory)}${
          stats.maxmemory ? ` / ${formatBytes(stats.maxmemory)}` : ''
        }`
      : '-'
  const evicted = stats?.evicted_keys != null ? formatNumber(stats.evicted_keys) : '-'

  return (
    <div className="mb-6 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
      <Tile label="Hit rate" value={hitRate} sub="keyspace hits / (hits + misses)" />
      <Tile label="Total keys" value={totalKeys} sub="whole Redis db (all namespaces)" />
      <Tile
        label="Memory used"
        value={memory}
        sub={
          stats?.used_memory_ratio != null
            ? `${(stats.used_memory_ratio * 100).toFixed(1)}% of max`
            : 'no max configured'
        }
      />
      <Tile label="Evicted keys" value={evicted} sub="since server start" />
    </div>
  )
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-[8px] bg-[var(--bg-subtle)] px-[18px] py-4">
      <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </p>
      <p className="font-display text-[26px] leading-none text-[var(--ink)]">{value}</p>
      {sub ? <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{sub}</p> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty / unavailable states
// ---------------------------------------------------------------------------

function Unavailable({ reason, backend }: { reason?: string; backend?: string }) {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-6 py-16 text-center"
      data-testid="cache-unavailable"
    >
      <ServerOff className="h-5 w-5 text-[var(--ink-3)]" />
      <p className="text-[13px] text-[var(--ink-2)]">Cache observability unavailable</p>
      <p className="max-w-[520px] text-[12px] text-[var(--ink-3)]">
        {reason ??
          'The read-through cache observability tool requires a Redis backend.'}
      </p>
      {backend ? (
        <p className="text-[11px] text-[var(--ink-3)]">
          Active cache backend: <span className="font-mono">{backend}</span>
        </p>
      ) : null}
    </div>
  )
}

function Empty({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      <Inbox className="h-4 w-4 text-[var(--ink-3)]" />
      <p className="text-[12px] text-[var(--ink-3)]">{message}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Columns + helpers
// ---------------------------------------------------------------------------

function cacheRowKey(r: CacheKeyRow): string {
  return [r.kind, r.namespace, r.tenant_id, r.entity_type, r.object_id, r.version, r.variant]
    .map((p) => p ?? '')
    .join(':')
}

function buildColumns(): DataTableColumn<CacheKeyRow>[] {
  return [
    {
      id: 'tenant',
      header: 'Tenant',
      cell: (r) => (
        <span className="text-[var(--ink)]">
          {r.tenant_name ?? `Tenant ${r.tenant_id}`}
        </span>
      ),
    },
    {
      id: 'namespace',
      header: 'Namespace',
      cell: (r) => (
        <span className="font-mono text-[11.5px] text-[var(--ink-2)]">
          {r.namespace}
        </span>
      ),
    },
    {
      id: 'entity_type',
      header: 'Entity Type',
      cell: (r) => (
        <span className="text-[var(--ink-2)]">{r.entity_type ?? '-'}</span>
      ),
    },
    {
      id: 'object_id',
      header: 'Object ID',
      cell: (r) => (
        <span
          className="block max-w-[240px] truncate font-mono text-[11px] text-[var(--ink-3)]"
          title={r.object_id ?? undefined}
        >
          {r.object_id ?? '-'}
        </span>
      ),
    },
    {
      id: 'variant',
      header: 'Variant',
      cell: (r) => (
        <span className="font-mono text-[11px] text-[var(--ink-3)]">
          {r.variant ?? '-'}
        </span>
      ),
    },
    {
      // Column ids match the CacheKeyRow data key so sortRows (which sorts by
      // the emitted sort field) reads the right value.
      id: 'version',
      header: 'Version',
      sortable: true,
      cell: (r) =>
        r.kind === 'data' && r.version != null ? (
          <Badge variant="secondary">v{r.version}</Badge>
        ) : (
          <Badge variant="outline">counter</Badge>
        ),
    },
    {
      id: 'ttl_seconds',
      header: 'TTL',
      sortable: true,
      cell: (r) => (
        <span className="font-mono text-[11px] text-[var(--ink-2)]">
          {formatTtl(r.ttl_seconds)}
        </span>
      ),
    },
    {
      id: 'size_bytes',
      header: 'Size',
      sortable: true,
      cell: (r) => (
        <span className="font-mono text-[11px] text-[var(--ink-2)]">
          {formatBytes(r.size_bytes)}
        </span>
      ),
    },
  ]
}

function sortRows(rows: CacheKeyRow[], sorting: SortingState | null): CacheKeyRow[] {
  if (!sorting) return rows
  const { field, direction } = sorting
  const dir = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = a[field as keyof CacheKeyRow]
    const bv = b[field as keyof CacheKeyRow]
    // Nulls always sort last, regardless of direction.
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
    return String(av).localeCompare(String(bv)) * dir
  })
}

function formatBytes(n: number | null | undefined): string {
  if (n == null) return '-'
  if (n < 1024) return `${n} B`
  const kb = n / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

function formatTtl(seconds: number | null | undefined): string {
  if (seconds == null) return '-'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
}
